(() => {
  console.log("✅ Cart drawer script loaded");

  const HOST_SELECTOR = "spark-drawer";
  const TERMS_HREF = "/policies/terms-of-service";
  const DISCOUNT_TEXT = "view discount or enter code";
  const PAYMENT_METHOD_TEXT = "payment method";

  // Track which roots we already observe so we can re-attach cleanly
  const observedRoots = new WeakSet();
  const observedHosts = new WeakSet();

  // ---- Your actions (safe + less brittle) ----
  function removeDiscountElement(root) {
    const span = [...root.querySelectorAll("span")].find(el =>
      (el.textContent || "").trim().toLowerCase().includes(DISCOUNT_TEXT)
    );
    if (!span) return;

    // Prefer a stable wrapper if possible
    const wrapper =
      span.closest("button, a, li, div, section, label, details") ||
      span.parentElement;

    wrapper?.style?.setProperty("display", "none", "important");
  }

  function removePaymentMethodElement(root) {
    const h4 = [...root.querySelectorAll("h4")].find(el =>
      (el.textContent || "").trim().toLowerCase().includes(PAYMENT_METHOD_TEXT)
    );
    if (!h4) return;

    const wrapper =
      h4.closest("section, li, div") ||
      h4.parentElement?.parentElement;

    wrapper?.style?.setProperty("display", "none", "important");
  }

  function removedTCElement(root) {
    const a = root.querySelector(`a[href="${TERMS_HREF}"]`);
    if (!a) return;

    const wrapper =
      a.closest("div, li, label, section") ||
      a.parentElement;

    wrapper?.style?.setProperty("display", "none", "important");

    // Enable buttons if they truly use disabled
    root.querySelectorAll("button[disabled]").forEach(btn => {
      btn.disabled = false;
      btn.removeAttribute("disabled");
      btn.setAttribute("aria-disabled", "false");
    });

    // In case they use aria-disabled instead
    root.querySelectorAll('button[aria-disabled="true"]').forEach(btn => {
      btn.setAttribute("aria-disabled", "false");
      btn.style.pointerEvents = "auto";
    });
  }

  function applyAll(root) {
    removeDiscountElement(root);
    removePaymentMethodElement(root);
    removedTCElement(root);
  }

  // ---- Utility: schedule apply (debounced to next frame) ----
  function makeScheduler(fn) {
    let raf = 0;
    return () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try { fn(); } catch (e) { console.warn("apply error:", e); }
      });
    };
  }

  // ---- Observe a root (and nested shadow roots) ----
  function observeRoot(root) {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);

    const schedule = makeScheduler(() => {
      applyAll(root);

      // ALSO apply to any nested open shadow roots inside this root
      root.querySelectorAll("*").forEach(el => {
        if (el.shadowRoot) applyAll(el.shadowRoot);
      });
    });

    // Run immediately
    schedule();

    // Re-run whenever Lit re-renders / DOM changes
    const mo = new MutationObserver(schedule);
    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "open", "hidden", "aria-hidden", "disabled", "aria-disabled"]
    });
  }

  // ---- Detect “open” changes + re-acquire current root each time ----
  function observeHost(host) {
    if (!host || observedHosts.has(host)) return;
    observedHosts.add(host);

    const schedule = makeScheduler(() => {
      // On every relevant change, (re)observe current shadow root and apply
      const root = host.shadowRoot;
      if (root) observeRoot(root);

      // Also try nested roots in case the real content is deeper
      host.querySelectorAll("*").forEach(el => {
        if (el.shadowRoot) observeRoot(el.shadowRoot);
      });
    });

    // initial
    schedule();

    // Watch host attribute/class changes (open/close often toggles here)
    const hostObs = new MutationObserver(schedule);
    hostObs.observe(host, {
      attributes: true,
      attributeFilter: ["class", "open", "opened", "active", "aria-hidden", "hidden"]
    });

    // Some components fire events on open/close
    host.addEventListener("toggle", schedule, true);
    host.addEventListener("open", schedule, true);
    host.addEventListener("opened", schedule, true);
    host.addEventListener("close", schedule, true);
    host.addEventListener("closed", schedule, true);

    // If the drawer is destroyed and recreated, we’ll catch it via the global observer below.
  }

  // ---- Global observer: handles drawer being created/destroyed ----
  function watchForDrawerForever() {
    const scan = () => {
      document.querySelectorAll(HOST_SELECTOR).forEach(observeHost);
    };

    scan();

    const domObs = new MutationObserver(scan);
    domObs.observe(document.documentElement, { childList: true, subtree: true });
  }

  watchForDrawerForever();
})();
